import type { QuoteTemplate } from '@common/types/quoteTemplates';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import FullScreenLoader from '@/components/FullScreenLoader';
import axiosInstance from '@/services/axiosInstance';
import { isRetryableClientLoadError } from '@/utils/queryError';
import { applyGoogleTranslateDomPatch } from './applyGoogleTranslateDomPatch';
import ClientErrorPage from './ClientErrorPage';
import ClientFormErrorBoundary from './ClientFormErrorBoundary';
import IntakeFormView from './IntakeFormView/IntakeFormView';
import IntakeFormViewV2 from './IntakeFormView/IntakeFormViewV2';

// Before any intake subtree renders — avoids NotFoundError from Google Translate DOM mutations.
applyGoogleTranslateDomPatch();

export default function IntakeFormParent() {
    const { intakeTemplateId } = useParams();

    const { data, error, isError, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['template-form-practice-area', intakeTemplateId],
        queryFn: async () => {
            if (!intakeTemplateId) return null;
            const response = await axiosInstance.get<
                Pick<QuoteTemplate, 'practiceArea' | 'process'>
            >(`api/v1/template-forms/${intakeTemplateId}/practice-area`);
            return response.data;
        },
        enabled: !!intakeTemplateId,
        staleTime: Infinity,
    });

    const practiceArea = data?.practiceArea;
    const matterType = data?.process;

    if (!intakeTemplateId) {
        return <ClientErrorPage />;
    }

    if (isError) {
        const canRetry = isRetryableClientLoadError(error);
        return (
            <ClientErrorPage
                onRetry={canRetry ? () => void refetch() : undefined}
                isRetrying={canRetry && isFetching}
            />
        );
    }

    return (
        <ClientFormErrorBoundary>
            <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
                {isLoading || !practiceArea || !matterType ? (
                    <FullScreenLoader />
                ) : practiceArea === 'RealEstate' &&
                  ['Sale', 'Purchase', 'Refinance', 'PrivateLender'].includes(matterType) ? (
                    <IntakeFormView />
                ) : practiceArea !== 'IDV' ? (
                    <IntakeFormViewV2 practiceArea={practiceArea} matterType={matterType} />
                ) : (
                    <ClientErrorPage />
                )}
            </div>
        </ClientFormErrorBoundary>
    );
}
